import React, { useEffect, useState } from 'react'
import { Box, Typography, TextField, Button, Stack } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import {
  setExamEmailError,
  resetExamEmailForm,
  setExamEmailLoading,
} from 'src/redux/slices/eExamination'
import { validateEmail } from 'src/utils/lib'
import { requestExamOtp, changeExamEmail } from './event'
import { closeDialog } from '../../../redux/slices/alertDialog'
import { Row } from 'src/components/Layout/Styled'

const ChangeEmailForm = ({ index = 0 }) => {
  const dispatch = useDispatch()

  const { examiner, isLoading } = useSelector(
    (state) => ({
      examiner: state.eExamination.examiners[index],
      isLoading: state.eExamination.isLoading,
    }),
    shallowEqual
  )

  const [step, setStep] = useState('inputEmail')
  const [otp, setOtp] = useState('')
  const [otpRef, setOtpRef] = useState('')
  const [newEmail, setNewEmail] = useState('')

  const onCancel = () => {
    dispatch(closeDialog())
    dispatch(resetExamEmailForm())
    setStep('inputEmail')
    setNewEmail('')
    setOtp('')
  }

  useEffect(() => {
    dispatch(resetExamEmailForm())
  }, [])

  const handleRequestOtp = async () => {
    dispatch(setExamEmailLoading(true))
    const payload = await dispatch(
      requestExamOtp({
        newEmail,
        examinerUuid: examiner.uuid,
      })
    )
    dispatch(setExamEmailLoading(false))

    setOtp('')
    if (payload?.data?.status === 'SUCCESS') {
      dispatch(setExamEmailError({ path: 'newEmail', message: '' }))
      setStep('newEmail')
      setOtpRef(payload.data.code)
    } else {
      dispatch(
        setExamEmailError({ path: 'newEmail', message: payload?.data?.message })
      )
    }
  }

  const handleConfirmOtp = () => {
    dispatch(setExamEmailLoading(true))
    dispatch(
      changeExamEmail({
        newEmail,
        uuid: examiner.uuid,
        oldEmail: examiner.email,
        otp,
      })
    ).finally(() => {
      dispatch(setExamEmailLoading(false))
    })
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: step === 'inputEmail' ? 475 : 500,
        mx: 'auto',
        textAlign: 'center',
        px: 2,
        py: 4,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: '#00008F',
          fontWeight: 'bold',
          mb: 4,
        }}
      >
        {step === 'inputEmail' ? 'แก้ไข' : 'ยืนยัน'}
      </Typography>

      {step === 'inputEmail' ? (
        <>
          <Box sx={{ textAlign: 'left', mb: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
              E-mail ใหม่
            </Typography>
            <TextField
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              fullWidth
              placeholder="example@email.com"
              error={!validateEmail(newEmail) && newEmail !== ''}
              helperText={
                !validateEmail(newEmail) && newEmail !== ''
                  ? 'กรุณากรอกอีเมลที่ถูกต้อง'
                  : ' '
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: 48,
                },
              }}
            />
          </Box>

          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            mt={1}
            mb={1}
          >
            <Button
              variant="outlined"
              onClick={onCancel}
              sx={{
                width: 220,
                height: 44,
                fontWeight: 'bold',
                color: '#00008F',
                borderColor: '#00008F',
              }}
            >
              ยกเลิก
            </Button>
            <LoadingButton
              variant="contained"
              onClick={handleRequestOtp}
              disabled={!validateEmail(newEmail)}
              loading={isLoading}
              sx={{
                width: 220,
                height: 44,
                fontWeight: 'bold',
                backgroundColor: '#00008F',
                '&.Mui-disabled': {
                  backgroundColor: '#E0E0E0',
                  color: '#9E9E9E',
                },
              }}
            >
              ส่ง
            </LoadingButton>
          </Stack>
        </>
      ) : (
        <>
          <Typography
            variant="body1"
            sx={{
              textAlign: 'left',
              mb: 1,
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
            กรุณากรอกรหัสยืนยัน ที่ถูกส่งไปยังอีเมล{' '}
            <Box component="span" sx={{ fontWeight: 'bold' }}>
              {newEmail}
            </Box>
          </Typography>

          <Typography variant="body1" sx={{ mb: 1 }}>
            รหัสอ้างอิง : {otpRef}
          </Typography>

          <Box sx={{ textAlign: 'left', mb: 3 }}>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
              รหัสยืนยัน
            </Typography>
            <TextField
              value={otp}
              onChange={(e) =>
                setOtp(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))
              }
              fullWidth
              placeholder="กรุณากรอกรหัสยืนยัน"
              sx={{
                '& .MuiOutlinedInput-root': { height: 48 },
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ textAlign: 'left' }}>
              ระบบจะส่งรหัสยืนยันไปยังอีเมลที่ระบุ{' '}
            </Typography>
            <Row>
              <Typography variant="body1" sx={{ textAlign: 'left' }}>
                ไม่ได้รับอีเมล?{' '}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  textDecoration: 'underline',
                  color: '#00008F',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
                onClick={handleRequestOtp}
              >
                ขอรหัส OTP ใหม่
              </Typography>
            </Row>
          </Box>

          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="outlined"
              onClick={onCancel}
              sx={{
                width: 230,
                height: 44,
                fontWeight: 'bold',
                color: '#00008F',
                borderColor: '#00008F',
              }}
            >
              ยกเลิก
            </Button>
            <LoadingButton
              variant="contained"
              onClick={handleConfirmOtp}
              loading={isLoading}
              disabled={otp.length !== 6}
              sx={{
                width: 230,
                height: 44,
                fontWeight: 'bold',
                backgroundColor: '#00008F',
                '&.Mui-disabled': {
                  backgroundColor: '#E0E0E0',
                  color: '#9E9E9E',
                },
              }}
            >
              ยืนยัน
            </LoadingButton>
          </Stack>
        </>
      )}
    </Box>
  )
}

export default ChangeEmailForm
